import React from 'react'
import { observer } from 'mobx-react-lite'

import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'

import { useGlobalStore, useScreenSize, useWidgetLoadTimer } from '../common'

import { Banner } from '../common/components/Banner'

import Greeting from './Greeting'
import { DashboardLocation } from './constants/Dashboard'
import type { DashboardWidget } from './types/DashboardTypes'

export interface DashboardProps {
  widgets: DashboardWidget[]
  isLoading: boolean
}

const Dashboard: React.FC<DashboardProps> = ({ widgets, isLoading }) => {
  const { isDesktop, isMobile } = useScreenSize()
  const { copyStore } = useGlobalStore()

  useWidgetLoadTimer({
    widgetName: 'Dashboard',
    isLoaded: !isLoading,
  })

  const sortedWidgets = React.useMemo(
    () =>
      widgets
        .filter((widget) => widget.location !== DashboardLocation.Disabled)
        .sort((w1, w2) => w1.index - w2.index),
    [widgets],
  )

  const mainWidgets = React.useMemo(
    () => sortedWidgets.filter((widget) => widget.location === DashboardLocation.Main),
    [sortedWidgets],
  )

  const sideWidgets = React.useMemo(
    () => sortedWidgets.filter((widget) => widget.location === DashboardLocation.RightSide),
    [sortedWidgets],
  )

  return (
    <Grid container={true} spacing={isMobile ? 0 : 24}>
      <Grid size={12} sx={{ pt: 16 }}>
        {copyStore.isInitialized && <Greeting />}
      </Grid>

      {/* Main Column */}
      <Grid size={{ xs: 12, lg: 8 }}>
        <Banner />
        {isLoading && (
          <Stack direction="column" spacing={24}>
            <Skeleton height={100} variant="rounded" width="100%" />
            <Skeleton height={300} variant="rounded" width="100%" />
            <Skeleton height={300} variant="rounded" width="100%" />
          </Stack>
        )}
        {!isLoading && (
          <Stack direction="column" spacing={24}>
            {isDesktop
              ? mainWidgets.map((widget) => (
                  <React.Fragment key={widget.index}>{widget.component}</React.Fragment>
                ))
              : sortedWidgets.map((widget) => (
                  <React.Fragment key={widget.index}>{widget.component}</React.Fragment>
                ))}
          </Stack>
        )}
      </Grid>

      {/* Right Column */}
      {isDesktop && (
        <Grid size={{ lg: 4 }}>
          {isLoading ? (
            <Stack direction="column" spacing={24}>
              <Skeleton height={200} variant="rounded" width="100%" />
              <Skeleton height={100} variant="rounded" width="100%" />
              <Skeleton height={300} variant="rounded" width="100%" />
            </Stack>
          ) : (
            <Stack direction="column" spacing={24}>
              {sideWidgets.map((widget) => (
                <React.Fragment key={widget.index}>{widget.component}</React.Fragment>
              ))}
            </Stack>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default observer(Dashboard)
